import { useMutation } from '@tanstack/react-query';
import { useState, type JSX } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { platformRoleLabel, type PlatformAdminRole } from '@/lib/permissions';

import { RoleChoice } from './RoleChoice';
import {
  ADMIN_REASON_LIMITS,
  updatePlatformAdminRole,
  type PlatformAdmin,
} from '../api/platform-admins.graphql';

interface Props {
  admin: PlatformAdmin;
  isSelf: boolean;
  onDone: (admin: PlatformAdmin) => void;
  onCancel: () => void;
}

/** OS-1717 — cambio de rol con motivo. El API impide dejar la plataforma sin un SUPER activo. */
export function ChangeRoleDialog({ admin, isSelf, onDone, onCancel }: Props): JSX.Element {
  const [role, setRole] = useState<PlatformAdminRole>(admin.role);
  const mutation = useMutation({
    mutationFn: (reason: string) => updatePlatformAdminRole(admin.id, role, reason),
    onSuccess: onDone,
  });

  return (
    <ConfirmDialog
      title={`Cambiar rol de ${admin.email}`}
      confirmLabel="Cambiar rol"
      pending={mutation.isPending}
      confirmDisabled={role === admin.role}
      error={mutation.error?.message ?? null}
      reason={{
        label: 'Motivo (queda en la auditoría)',
        placeholder: 'Ej.: pasa del equipo de soporte al comercial',
        ...ADMIN_REASON_LIMITS,
      }}
      onConfirm={(reason) => mutation.mutate(reason)}
      onCancel={onCancel}
    >
      <p>Rol actual: {platformRoleLabel(admin.role)}. El cambio rige en su siguiente acción, sin cerrar su sesión.</p>
      <RoleChoice value={role} onChange={setRole} legend="Nuevo rol" />
      {isSelf && admin.role === 'PLATFORM_ADMIN' && role !== 'PLATFORM_ADMIN' ? (
        <p role="note">
          Estás quitándote el rol Super: perderás el acceso a esta sección en cuanto se guarde.
        </p>
      ) : null}
    </ConfirmDialog>
  );
}
